import { Link } from "@inertiajs/react"
import type { Video } from "@/types"
import { Badge } from "@/components/ui/badge"
import { OptimizedImage } from "@/components/ui/optimized-image"
import VideoActionsMenu from "@/components/dashboard/videos/video-actions-menu"

const FORMAT_LABELS: Record<Video["aspect_ratio"], string> = {
  portrait: "9:16",
  landscape: "16:9",
  square: "1:1",
}

const STATUS_LABELS: Record<Video["status"], string> = {
  draft: "In progress",
  scripted: "Scripted",
  rendered: "Rendered",
  published: "Published",
  archived: "Archived",
}

const STATUS_STYLES: Record<Video["status"], string> = {
  draft: "bg-stone/60 text-slate",
  scripted: "bg-gold/20 text-gold",
  rendered: "bg-green-100 text-green-700",
  published: "bg-green-100 text-green-700",
  archived: "bg-warm-grey/20 text-warm-grey",
}

const THUMB_SIZES: Record<Video["aspect_ratio"], string> = {
  portrait: "h-20 w-[45px]",
  landscape: "h-12 w-20",
  square: "size-16",
}

const WIZARD_STEPS: Record<string, { label: string; index: number }> = {
  photos: { label: "Photos", index: 0 },
  style: { label: "Style", index: 1 },
  script: { label: "Script", index: 2 },
  audio: { label: "Audio", index: 3 },
  branding: { label: "Branding", index: 4 },
  review: { label: "Review", index: 5 },
}

export default function VideoRow({
  video,
  currentRole,
}: {
  video: Video
  currentRole: string
}) {
  const step = WIZARD_STEPS[video.wizard_step] || { label: video.wizard_step, index: 0 }
  const progress = ((step.index + 1) / 6) * 100
  const isDraft = video.status === "draft"

  return (
    <div className="relative flex items-center">
    <Link
      href={`/dashboard/videos/${video.id}`}
      className="group flex flex-1 items-center gap-4 rounded-xl border border-stone/40 bg-white px-4 py-3 pr-12 transition-colors hover:border-red/30 hover:shadow-sm"
    >
      {/* Thumbnail */}
      <div className={`relative shrink-0 overflow-hidden rounded-lg bg-charcoal/[0.03] ${THUMB_SIZES[video.aspect_ratio]}`}>
        {video.hero_image_url ? (
          <OptimizedImage
            src={video.hero_image_url}
            alt=""
            className="size-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-gradient-to-br from-charcoal to-slate">
            <svg className="size-5 text-white/30" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
        )}
        {/* Format badge */}
        <div className="absolute bottom-1 right-1">
          <span className="rounded bg-black/50 px-1 py-0.5 text-[8px] font-semibold tabular-nums text-white/80 backdrop-blur-sm">
            {FORMAT_LABELS[video.aspect_ratio]}
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-charcoal group-hover:text-red">
            {video.name}
          </p>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <Badge className={`shrink-0 text-[10px] ${STATUS_STYLES[video.status]}`}>
            {STATUS_LABELS[video.status]}
          </Badge>
          {video.listing_address && (
            <span className="truncate text-xs text-warm-grey">{video.listing_address}</span>
          )}
        </div>
        {isDraft && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="h-1 w-24 overflow-hidden rounded-full bg-stone/40">
              <div
                className="h-full rounded-full bg-red/70"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-[10px] text-warm-grey">{step.label}</span>
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="hidden shrink-0 items-center gap-3 text-xs text-warm-grey sm:flex">
        <span>{video.target_length}s</span>
        {video.script_count > 0 && (
          <span>{video.script_count} {video.script_count === 1 ? "script" : "scripts"}</span>
        )}
        <span>{FORMAT_LABELS[video.aspect_ratio]}</span>
        <span>{formatRelativeTime(video.updated_at)}</span>
      </div>

    </Link>
    <div className="absolute right-2">
      <VideoActionsMenu video={video} currentRole={currentRole} />
    </div>
    </div>
  )
}

function formatRelativeTime(dateStr: string | Date): string {
  const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}
